// The root URL is the first introduction. /home keeps the site's navigation
// present and uses a shorter arrival when a visitor returns.
let introductionClaimed = false;
let requestedIntroduction = null;

// The sidebar identity explicitly replays the introduction. Keep that intent
// out of history so Back and Forward retain their normal reading positions.
export function requestHomeIntroduction() {
  const request = {};
  requestedIntroduction = request;
  return () => {
    if (requestedIntroduction === request) requestedIntroduction = null;
  };
}

export function consumeHomeIntroduction(hash = '') {
  const replay = Boolean(requestedIntroduction) && isHomeIntroduction(hash);
  requestedIntroduction = null;
  return replay;
}

export function isHomeIntroduction(hash = '') {
  return /^(?:#\/?(?:\?[^#]*)?)?$/.test(hash);
}

export function claimHomeEntrance({ hash = '', initialHash = hash, chapter = '', restored = false, reduced = false, replay = false } = {}) {
  const introduction = isHomeIntroduction(hash) && (replay || (isHomeIntroduction(initialHash) && !introductionClaimed));
  introductionClaimed = true;
  if (chapter || restored || reduced) return 'settled';
  return introduction ? 'intro' : 'home';
}

export function homeEntranceTiming(part, index = 0, mode = 'intro', mobile = false) {
  const timings = {
    scene: [0, 980, mobile ? 0 : 10],
    name: [70 + index * 70, 640, 18],
    creed: [270 + index * 85, 530, 10],
    portrait: [340, 720, mobile ? 8 : 14],
    context: [530 + index * 60, 440, 7],
    welcome: [650, 440, 5],
    ask: [730, 440, 7],
    header: [900, 460, -12],
    rail: [1030, 440, 14],
    cue: [1130, 460, 6],
  };
  const [delay, duration, distance] = timings[part] || [0, 400, 6];
  const returning = mode === 'home';
  return {
    delay: Math.round(delay * (returning ? 0.48 : 1)),
    duration: Math.round(duration * (returning ? 0.68 : 1)),
    distance: distance * (returning ? 0.65 : 1),
  };
}

export function initHomeEntrance(root, { mode = 'home', onReady = () => {} } = {}) {
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 760px)').matches;
  const header = document.querySelector('.container > .header');
  const animations = new Set();
  const listeners = [];
  let stopped = false;
  const initialScroll = window.scrollY;
  const initialWidth = window.innerWidth;
  const listen = (target, type, fn, options) => {
    target.addEventListener(type, fn, options);
    listeners.push(() => target.removeEventListener(type, fn, options));
  };
  const finish = () => {
    if (stopped) return;
    stopped = true;
    listeners.splice(0).forEach(remove => remove());
    animations.forEach(animation => animation.cancel());
    animations.clear();
  };
  const ready = () => {
    onReady();
    window.dispatchEvent(new Event('home-entrance-prepared'));
  };
  if (mode === 'settled' || preference.matches || initialScroll > 80 || !root.animate) {
    ready();
    return finish;
  }

  const elements = [...root.querySelectorAll('[data-home-enter]')];
  if (mode === 'intro' && header) elements.push(header);
  const counts = new Map();
  const start = document.timeline?.currentTime;
  try {
    elements.forEach(element => {
      // Hidden desktop/mobile counterparts never consume a stagger slot.
      if (!element.getClientRects().length || !element.offsetWidth || !element.offsetHeight) return;
      const part = element === header ? 'header' : element.dataset.homeEnter;
      const index = counts.get(part) || 0;
      counts.set(part, index + 1);
      const { delay, duration, distance } = homeEntranceTiming(part, index, mode, mobile);
      const frames = { opacity: [0, 1] };
      // Keep the rail's existing translateY(-50%) centring while it enters.
      if (part === 'rail') frames.translate = [`${distance}px 0px`, '0px 0px'];
      else if (distance) frames.transform = [`translateY(${distance}px)`, 'translateY(0px)'];
      if (part === 'portrait') frames.transform = [`translateY(${distance}px) rotate(${mobile ? 2 : -2}deg)`, 'translateY(0px) rotate(0deg)'];
      const animation = element.animate(frames, {
        delay, duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both',
      });
      animation.id = `home-entrance-${part}-${index}`;
      if (Number.isFinite(start)) animation.startTime = start;
      animations.add(animation);
      animation.finished.then(() => {
        animation.cancel();
        animations.delete(animation);
        if (!animations.size) finish();
      }).catch(() => {});
    });
  } catch {
    finish();
  }
  // The first keyframes already cover the pending CSS state before Vue
  // releases it. No element paints at full opacity and then disappears.
  ready();
  if (stopped || !animations.size) return finish;
  listen(window, 'scroll', () => { if (Math.abs(window.scrollY - initialScroll) > 16) finish(); }, { passive: true });
  listen(window, 'resize', () => { if (Math.abs(window.innerWidth - initialWidth) > 40) finish(); }, { passive: true });
  listen(document, 'visibilitychange', () => { if (document.hidden) finish(); });
  listen(preference, 'change', finish);
  listen(root, 'pointerdown', finish, { passive: true });
  listen(root, 'focusin', event => { if (event.target.closest('a, button, input, textarea, select')) finish(); });
  if (mode === 'intro' && header) {
    listen(header, 'pointerdown', finish, { passive: true });
    listen(header, 'focusin', finish);
  }
  return finish;
}
